
<template>
  <div class="flex flex-col h-full min-h-0 p-6 space-y-5 bg-gray-50">
    <!-- Search toolbar -->
    <div class="grid gap-3 p-3 bg-white border border-gray-100 shadow-sm rounded-2xl sm:grid-cols-2">
      <!-- Location search -->
      <div class="relative godview-location">
        <span class="absolute z-10 flex items-center text-xl text-gray-400 pointer-events-none left-3 top-2.5 material-icons-round">place</span>
        <LocationSearchBox v-model="locationSearch" />
      </div>

      <!-- Driver search -->
      <div class="relative">
        <div class="relative">
          <span class="absolute inset-y-0 left-0 z-10 flex items-center pl-3 text-xl text-gray-400 pointer-events-none material-icons-round">search</span>
          <input
            v-model="searchText"
            @keyup="searchDriver"
            placeholder="Search driver by name"
            class="godview-input block w-full"
          />
        </div>
        <transition
          enter-active-class="transition duration-100 ease-out"
          enter-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-75 ease-in"
          leave-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0"
        >
          <div v-if="driverSearchResults.length" class="absolute inset-x-0 z-50 mt-2">
            <div class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64">
              <ul>
                <li
                  v-for="(result, index) in driverSearchResults"
                  :key="result._id"
                  @click="selectDriver(index)"
                  class="flex items-center px-3 py-2 space-x-3 cursor-pointer transition-colors duration-100 hover:bg-taxiye-50"
                >
                  <img
                    class="object-cover w-10 h-10 border border-gray-100 rounded-full"
                    :src="result.profileImage"
                    :alt="`${result.firstName} ${result.lastName}`"
                  />
                  <div class="min-w-0">
                    <p class="font-medium text-gray-900 truncate">{{ `${result.firstName} ${result.lastName}` }}</p>
                    <p class="text-sm text-gray-500">{{ result.phoneNumber }}</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Stat / filter cards -->
    <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <!-- Active trips -->
      <div class="relative flex items-center p-5 space-x-4 overflow-hidden bg-white rounded-2xl shadow-md">
        <div class="flex items-center justify-center flex-shrink-0 w-14 h-14 rounded-xl bg-taxiye-100 text-taxiye-600">
          <i class="text-3xl material-icons-round">loop</i>
        </div>
        <div class="min-w-0">
          <p class="text-xs font-semibold tracking-wide text-gray-700 uppercase truncate">Active Trips</p>
          <p class="text-3xl font-extrabold leading-tight text-gray-900">{{ stats.totalRunningTrips || 0 }}</p>
        </div>
      </div>

      <!-- Online (filter) -->
      <button
        type="button"
        @click="filters.online = !filters.online"
        class="stat-filter group relative flex items-center p-5 space-x-4 overflow-hidden text-left transform bg-white border border-transparent rounded-2xl shadow-md transition-all duration-200 hover:-translate-y-1 hover:shadow-xl focus:outline-none"
      >
        <div class="flex items-center justify-center flex-shrink-0 w-14 h-14 text-green-600 bg-green-100 rounded-xl">
          <i class="text-3xl material-icons-round">local_taxi</i>
        </div>
        <div class="min-w-0">
          <p class="flex items-center text-xs font-semibold tracking-wide text-gray-700 uppercase truncate">
            <span class="w-2 h-2 mr-1.5 bg-green-500 rounded-full"></span>Online Fleets
          </p>
          <p class="text-3xl font-extrabold leading-tight text-gray-900">{{ stats.totalActiveFleets || 0 }}</p>
        </div>
        <i class="absolute text-base text-gray-300 transition-colors duration-150 top-3 right-3 material-icons-round group-hover:text-gray-400">{{ filters.online ? 'visibility' : 'visibility_off' }}</i>
      </button>

      <!-- Offline (filter) -->
      <button
        type="button"
        @click="filters.offline = !filters.offline"
        class="stat-filter group relative flex items-center p-5 space-x-4 overflow-hidden text-left transform bg-white border border-transparent rounded-2xl shadow-md transition-all duration-200 hover:-translate-y-1 hover:shadow-xl focus:outline-none"
        :class="filters.offline ? 'border-red-400' : 'border-transparent'"
      >
        <div class="flex items-center justify-center flex-shrink-0 w-14 h-14 text-red-500 bg-red-100 rounded-xl">
          <i class="text-3xl material-icons-round">local_taxi</i>
        </div>
        <div class="min-w-0">
          <p class="flex items-center text-xs font-semibold tracking-wide text-gray-700 uppercase truncate">
            <span class="w-2 h-2 mr-1.5 bg-red-500 rounded-full"></span>Offline Fleets
          </p>
          <p class="text-3xl font-extrabold leading-tight text-gray-900">{{ stats.activeButOfflineVehicles || 0 }}</p>
        </div>
        <i class="absolute text-base text-gray-300 transition-colors duration-150 top-3 right-3 material-icons-round group-hover:text-gray-400">{{ filters.offline ? 'visibility' : 'visibility_off' }}</i>
      </button>

      <!-- In active trip (filter) -->
      <button
        type="button"
        @click="filters.inActiveTrip = !filters.inActiveTrip"
        class="stat-filter group relative flex items-center p-5 space-x-4 overflow-hidden text-left transform bg-white border border-transparent rounded-2xl shadow-md transition-all duration-200 hover:-translate-y-1 hover:shadow-xl focus:outline-none"
      >
        <div class="flex items-center justify-center flex-shrink-0 w-14 h-14 text-blue-500 bg-blue-100 rounded-xl">
          <i class="text-3xl material-icons-round">local_taxi</i>
        </div>
        <div class="min-w-0">
          <p class="flex items-center text-xs font-semibold tracking-wide text-gray-700 uppercase truncate">
            <span class="w-2 h-2 mr-1.5 bg-blue-500 rounded-full"></span>In Active Trip
          </p>
          <p class="text-3xl font-extrabold leading-tight text-gray-900">{{ stats.inActiveTrip || 0 }}</p>
        </div>
        <i class="absolute text-base text-gray-300 transition-colors duration-150 top-3 right-3 material-icons-round group-hover:text-gray-400">{{ filters.inActiveTrip ? 'visibility' : 'visibility_off' }}</i>
      </button>
    </div>

    <!-- Map -->
    <div
      class="min-h-0 overflow-hidden bg-white border border-gray-100 shadow-sm"
      :class="isFullscreen ? 'fixed inset-0 z-50 rounded-none' : 'relative flex-1 rounded-2xl'"
    >
      <!-- Map controls overlay -->
      <div class="absolute z-10 flex items-center space-x-3 top-4 right-4">
        <!-- Heat map toggle -->
        <div class="flex items-center px-3 py-2 space-x-3 bg-white border border-gray-100 shadow-md rounded-xl">
          <span class="flex items-center text-sm font-semibold text-gray-700">
            <i class="mr-1.5 text-lg text-taxiye-500 material-icons-round">whatshot</i>Heat Map
          </span>
          <div class="relative inline-block w-16 align-middle transition duration-200 ease-in select-none">
            <input
              type="checkbox"
              name="toggle"
              id="toggle-payment"
              v-model="showHeatMap"
              class="absolute block w-8 h-8 bg-white border-4 rounded-full appearance-none cursor-pointer toggle-checkbox"
            />
            <label
              for="toggle-payment"
              class="block h-8 overflow-hidden bg-gray-300 rounded-full cursor-pointer toggle-label"
            ></label>
          </div>
        </div>

        <!-- Fullscreen toggle -->
        <button
          type="button"
          @click="toggleFullscreen"
          :title="isFullscreen ? 'Exit full screen' : 'Full screen'"
          class="flex items-center justify-center bg-white border border-gray-100 shadow-md w-11 h-11 rounded-xl text-gray-600 transition-colors duration-150 hover:bg-gray-50 hover:text-taxiye-600 focus:outline-none"
        >
          <i class="text-xl material-icons-round">{{ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</i>
        </button>
      </div>

      <MapBoxWithMarkers
        :markers="markers"
        :mapCenter="mapCenter"
        :mapZoom="mapZoom"
        :popupData="infoWindowData"
        @markerClicked="markerClicked"
      ></MapBoxWithMarkers>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import LocationSearchBox from "@/components/LocationSearchBox";
import init from "@/gmaps";

import MapBoxWithMarkers from "@/components/MapBoxWithMarkers";

export default {
  data() {
    return {
      drivers: [],
      google: null,
      // markers: {},
      infowindows: {},
      map: null,
      interval: null,
      stats: {},
      loadingCurrentDriverInfo: false,
      currentDriverInfo: {},
      currentVehicleInfo: {},
      locationSearch: null,
      driverSearchResults: [],
      searchText: "",
      driverId: null,
      filters: {
        online: true,
        offline: false,
        inActiveTrip: true,
      },
      locationData: [],
      showHeatMap: false,
      heatmap: {},
      markers: [],
      mapZoom: null,
      mapCenter: {
      },
      isFullscreen: false,
    };
  },
  async mounted() {
    this.google = await init();

    // if (this.$route.query.lat && this.$route.query.long && !isNaN(this.$route.query.lat) && !isNaN(this.$route.query.long)) {
    //   this.map = new this.google.maps.Map(this.$refs.map, {
    //     center: {
    //       lat: Number(this.$route.query.lat),
    //       lng: Number(this.$route.query.long),
    //     },
    //     zoom: 21,
    //     streetViewControl: false,
    //     mapTypeControl: false,

    //   });
    // } else {
    //   this.map = new this.google.maps.Map(this.$refs.map, {
    //     center: {
    //       lat: 9.004912,
    //       lng: 38.778282,
    //     },
    //     zoom: 13,
    //     streetViewControl: false,
    //   });
    // }

    if (this.$route.query.driver) {
      this.driverId = this.$route.query.driver;
      this.$misc
        .get("drivers/" + this.$route.query.driver)
        .then(({ data }) => {
          if (data.vehicle && data.vehicle.position) {
            const [long, lat] = data.vehicle.position.coordinates
            this.mapCenter = {
              lat: Number(lat),
              lng: Number(long)
            }
            this.mapZoom = 21
          }
          const { firstName, lastName, phoneNumber } = data.driver
          this.selectedDriver = `${firstName} ${lastName} - ${phoneNumber}`;
          this.selectedDriverObj = data.driver
        });
    }
  },
  components: {
    LocationSearchBox,
    MapBoxWithMarkers,
  },
  created() {
    this.getDrivers();
    this.getStats();
    this.interval = setInterval(() => {
      this.getDrivers();
      this.getStats();
    }, 20000);
    this.getLocationData()
    window.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    clearInterval(this.interval);
    window.removeEventListener("keydown", this.onKeydown);
  },
  computed: {
    infoWindowData() {
      return this.loadingCurrentDriverInfo ? "loading data... please wait..." : "<p>Full Name: " +
        this.currentDriverInfo.firstName + ' ' + this.currentDriverInfo.lastName +
        "</p>" +
        "<p>Vehicle ID: " +
        this.currentVehicleInfo._id +
        "</p>" +
        "<p>Vehicle Model: " +
        (this.currentDriverInfo.modelName
          ? this.currentDriverInfo.modelName
          : "-") +
        "</p>" +
        "<p>Color: " +
        this.currentVehicleInfo.color +
        "</p>" +
        "<p>Approved: " +
        this.currentDriverInfo.approved +
        "</p>" +
        "<p class='pb-2'>Rating: " +
        this.currentDriverInfo.rating +
        "</p>" +
        '<a href="/drivers/' +
        this.currentDriverInfo._id +
        '" target="_balnk" class="w-20 h-20 p-1 px-4 text-lg text-white rounded-md shadow-lg bg-taxiye-600 hover:bg-taxiye-400">locate driver</a>'
    }
  },
  watch: {
    locationData(data) {
      const heatmapData = data.map((d) => new this.google.maps.LatLng(d.lat, d.long));

      if (this.heatmap && this.heatmap.setMap) {
        this.heatmap.setMap(null);
      }

      this.heatmap = new this.google.maps.visualization.HeatmapLayer({
        data: heatmapData,
        radius: 40
      });


      // var overlay = new this.google.maps.OverlayView();

      // // Add the container when the overlay is added to the map.
      // overlay.onAdd = function() {
      //   var layer = d3.select(this.getPanes().overlayMouseTarget).append("div")
      //     .attr("class", "nodes");

      //   overlay.draw = function() {
      //     /* var projection = this.getProjection(),
      //       padding = 10;

      //     var marker = layer.selectAll("svg")
      //       .data(data)
      //       .each(transform)
      //       .enter().append("svg")
      //       .each(transform)
      //       .attr("class", "marker")
      //       .on("mouseover", function(d) {
      //         tooltip.transition()
      //           .duration(200)
      //           .style("opacity", 1);
      //         tooltip.html(d.node_id)
      //           .style("left", (d3.event.pageX) + "px")
      //           .style("top", (d3.event.pageY) + "px");
      //       })
      //       .on("mouseout", function(d) {
      //         tooltip.transition()
      //           .duration(500)
      //           .style("opacity", 0);
      //       });


      //     marker.append("circle")
      //       .attr("r", 7)
      //       .attr("cx", padding)
      //       .attr("cy", padding)
      //       .style("fill", function(d) {
      //         if (d.temp1) {
      //           return colorScale(d.temp1);
      //         } else
      //           return "none";
      //       });


      //     marker.append("text")
      //       .attr("x", padding + 7)
      //       .attr("y", padding)
      //       .attr("dy", ".31em")
      //       .text(function(d) {
      //         return d.temp1;
      //       });
      // */
      //     function transform(d) {
      //       d = new google.maps.LatLng(d.lat, d.lng);
      //       d = projection.fromLatLngToDivPixel(d);
      //       return d3.select(this)
      //         .style("left", (d.x - padding) + "px")
      //         .style("top", (d.y - padding) + "px");
      //     }
      //   };
      // };

      if (this.showHeatMap && this.heatmap && this.heatmap.setMap)
        this.heatmap.setMap(this.map);
    },
    showHeatMap(show) {
      if (this.heatmap && this.heatmap.setMap) {

        if (show) {
          this.heatmap.setMap(this.map)
        } else {
          this.heatmap.setMap(null)
        }
      }
    },
    $route: {
      handler() {
        // if (this.$route.query.driver) {
        //   this.driverId = this.$route.query.driver;
        //   this.$misc
        //     .get("drivers/" + this.$route.query.driver)
        //     .then(({ data }) => {
        //       const { firstName, lastName, phoneNumber } = data.driver
        //       this.selectedDriver = `${firstName} ${lastName} - ${phoneNumber}`;
        //       this.selectedDriverObj = data.driver
        //     });
        // }
        // if (this.$route.query.lat && this.$route.query.long && !isNaN(this.$route.query.lat) && !isNaN(this.$route.query.long)) {
        //   this.mapCenter = {
        //     lat: Number(this.$route.query.lat),
        //     lng: Number(this.$route.query.long),
        //   }
        //   this.mapZoom = 21
        // } else {
        //   this.mapCenter = {
        //     lat: 9.004912,
        //     lng: 38.778282,
        //   }
        //   this.mapZoom = 13
        // }
        if (this.$route.query.driver) {
          this.driverId = this.$route.query.driver;
          this.$misc
            .get("drivers/" + this.$route.query.driver)
            .then(({ data }) => {
              if (data.vehicle && data.vehicle.position) {
                const [long, lat] = data.vehicle.position.coordinates

                this.mapCenter = {
                  lat: Number(lat),
                  lng: Number(long)
                }
                this.mapZoom = 21
              }
              const { firstName, lastName, phoneNumber } = data.driver
              this.selectedDriver = `${firstName} ${lastName} - ${phoneNumber}`;
              this.selectedDriverObj = data.driver
            });
        }
      },
      deep: true,
    },
    filters: {
      handler() {
        this.updateMarkers(this.drivers);
      },
      deep: true
    },
    fetchDrivers() {
      this.fetchDrivers()
    },
    driverId() {
      let url = this.$route.path + '?'
      if (this.driverId)
        url += `&driver=${this.driverId}`
      if (!this.$route.driver || this.$route.driver != this.driverId)
        this.$router.push(url)
    },
    locationSearch(newValue) {
      if (newValue && newValue.coordinate) {
        let url = this.$route.path
        this.$router.push(url)
        // if (this.driverId)
        //   url += `&driver=${this.driverId}`
        // if (this.locationSearch) {
        //   if (this.locationSearch.lat)
        //     url += `&lat=${this.locationSearch.lat}`
        //   if (this.locationSearch.long)
        //     url += `&long=${this.locationSearch.long}`
        // }

        this.mapCenter = {
          lat: newValue.coordinate.lat,
          lng: newValue.coordinate.long,
        }
        this.mapZoom = 17
      }
      // this.$router.push(url)
    }
  },
  methods: {
    onKeydown(e) {
      if (e.key === "Escape" && this.isFullscreen) {
        this.toggleFullscreen();
      }
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      // Mapbox tracks the window resize event by default; dispatch one after the
      // container size change so the map reflows to fill/restore its new bounds.
      this.$nextTick(() => {
        window.dispatchEvent(new Event("resize"));
      });
    },
    markerClicked(id) {
      const d = this.drivers.find(x => x._id === id)

      this.loadingCurrentDriverInfo = true
      this.fetchDriverInfo(d, d.driver._id).then((result) => {
        this.loadingCurrentDriverInfo = false
        this.currentVehicleInfo = result.vehicle
        this.currentDriverInfo = result.driver
      })

    },
    delay(delayInms) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(10);
        }, delayInms);
      });
    },
    getStats() {
      this.$misc
        .get("dashboard")
        .then((response) => (this.stats = response.data))
        .catch((err) => console.log(err));
    },
    getLocationData() {
      this.$http
        .get("trip-searches/location-data")
        .then((response) => (this.locationData = response.data))
        .catch((err) => console.log(err));
    },
    searchDriver(e) {
      if (e.target.value.trim() === "") {
        this.selectedDriverObj = null;
        this.selectedDriver = '';
        this.driverId = null;
        this.driverSearchResults = [];
        const query = { ...this.$route.query, driver: null };
        this.$router.push({ query });
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.driverSearchResults = [];
          this.$misc
            .get("drivers/search?vehicle=true&limit=5&q=" + name)
            .then(({ data }) => {
              this.driverSearchResults = data;
            })
            .catch((err) => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.driverSearchResults = [];
        }
      }
    },
    selectDriver(index) {
      let driver = this.driverSearchResults[index];
      this.selectedDriverObj = driver;
      this.selectedDriver = `${driver.firstName} ${driver.lastName} - ${driver.phoneNumber}`;
      this.driverId = driver._id;
      this.driverSearchResults = [];
      const query = { ...this.$route.query, driver: this.driverId };
      this.$router.push({ query });
    },
    async getDrivers() {
      this.$misc
        .get("/godview")
        .then((response) => {
          this.drivers = response.data;
          this.updateMarkers(this.drivers);
        })
        .catch((err) => console.log(err));
    },
    fetchDrivers: _.debounce(function () {
      this.loading = true;

      let filter;

      if (this.selectedApprovedFilter === "approved") {
        filter = true;
      } else if (this.selectedApprovedFilter === "unapproved") {
        filter = false;
      } else {
        filter = null;
      }
      this.$misc
        .get(`/drivers/admin-search`, {
          params: {
            q: this.searchText,
            from: this.range.start ? this.range.start : undefined,
            to: this.range.end ? this.range.end : undefined,
            approved: filter,
            completed:
              this.completed && ["true", "false"].includes(this.completed)
                ? this.completed
                : "all",
            page: this.currentPage,
            limit: this.perPage,
          },
        })
        .then((response) => {
          this.drivers = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count,
          };
        })
        .catch((err) => console.log(err))
        .finally(() => setTimeout(() => (this.loading = false), 300));
    }, 500),
    async fetchDriverInfo(element, driverId) {
      const response = await this.$misc
        .get(`/drivers/${driverId}`)

      // console.log("@#$@#$@##@")
      // console.log(response.data)


      return response.data
    },
    updateMarkers(data) {
      // for (const [, v] of Object.entries(this.markers)) {
      //   v.setMap(null)
      // }
      this.markers = data.filter(x => {
        if (x.online && this.filters.online) {
          return true
        }
        else if ((!x.online && !x.inActiveTrip) && this.filters.offline) {
          return true
        }
        else if (x.inActiveTrip && this.filters.inActiveTrip) {
          return true
        } else {
          return false
        }
      }).map((element) => {
        element.driver = element.driver ? element.driver : {}
        const pinData = {}

        pinData.id = element._id

        if (element.position) {
          var color = "red"

          if (element.inActiveTrip === true) {
            color = "blue"
          }
          if (element.online === true) {
            color = "green";
          }

          pinData.coordinates = [element.position.coordinates[0], element.position.coordinates[1]]
          pinData.color = color
          pinData.title = element.driver
            ? element.driver.fullName
            : "-"

          // const infowindow = new this.google.maps.InfoWindow({
          //   content: "loading...",
          // });
          // this.markers[element._id].setMap(this.map);
          // this.markers[element._id].addListener("click", () => {
          //   this.loadingCurrentDriverInfo = true
          //   this.fetchDriverInfo(element, element.driver._id).then((result) => {
          //     infowindow.open(this.map, this.markers[element._id]);
          //     this.loadingCurrentDriverInfo = false
          //     this.currentVehicleInfo = result.vehicle
          //     this.currentDriverInfo = result.driver
          //     infowindow.setContent(this.infoWindowData);

          //   })
          // });
          // }
        }
        return pinData
      });
    },
  },
};
</script>
<style scoped>
.toggle-checkbox:checked {
  @apply right-0;
  @apply border-green-400;
  right: 0;
  border-color: #68d391;
}
.toggle-checkbox:checked + .toggle-label {
  @apply bg-green-400;
  background-color: #68d391;
}

/* Hide the LocationSearchBox's built-in "Search Place" label. */
.godview-location >>> span {
  display: none;
}

/* Both search fields share one look. The location field is a child component,
   so it's styled via the deep selector; both use identical values. */
.godview-input {
  margin-top: 0;
  padding: 0.625rem 0.75rem 0.625rem 2.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #374151;
  background-color: #f9fafb;
  border: 1px solid transparent;
  border-radius: 0.75rem;
  box-shadow: none;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease;
}
.godview-location >>> input {
  margin-top: 0;
  padding: 0.625rem 0.75rem 0.625rem 2.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #374151;
  background-color: #f9fafb;
  border: 1px solid transparent;
  border-radius: 0.75rem;
  box-shadow: none;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease;
}
.godview-input::placeholder,
.godview-location >>> input::placeholder {
  color: #9ca3af;
}
.godview-input:focus,
.godview-location >>> input:focus {
  background-color: #ffffff;
  border-color: #ff3131;
  box-shadow: 0 0 0 1px #ff3131;
  outline: none;
}
</style>